import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Trash2, AlertTriangle, CheckCircle, Shield, Database, CreditCard, XCircle } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { apiFetch, clearAuthSession } from '@/services/apiService';
import { CHALO_API_BASE } from '@/config/apiConfig';

const DeleteAccount = () => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const { t } = useLanguage();

  const handleDeleteAccount = async () => {
    if (!isConfirmed) {
      setDeleteError('Please confirm that you understand this action cannot be undone.');
      return;
    }

    setIsDeleting(true);
    setDeleteError('');
    
    try {
      const response = await apiFetch(`${CHALO_API_BASE}/delete-account`, {
        method: 'DELETE',
        headers: {
          'accept': '*/*',
        },
      });

      if (response.status === 200) {
        // Clear tokens from cookies
        clearAuthSession();
        
        // Show success modal
        setShowSuccessModal(true);
      } else {
        const errorText = await response.text();
        setDeleteError(
          errorText || 'Failed to delete account. Please try again later.'
        );
      }
    } catch (error) {
      console.error('Delete account error:', error);
      setDeleteError('Failed to delete account. Please check your connection and try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    // Redirect to home page
    window.location.href = '/';
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 tracking-tight">
                Delete Account
              </h1>
            </div>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
          </div>
          
          {/* Main Card */}
          <Card className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50 border-b border-gray-200 px-6 py-5">
              <CardTitle className="text-2xl text-red-700 flex items-center gap-3">
                <AlertTriangle className="w-6 h-6" />
                Confirm Account Deletion
              </CardTitle>
            </CardHeader>
            
            <CardContent className="p-6 space-y-6">
              {/* Warning Alert */}
              <Alert className="bg-red-50 border-2 border-red-200 rounded-xl">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <AlertDescription className="text-red-800">
                  <p className="font-bold text-base mb-3">Important: This action is permanent and irreversible</p>
                  <p className="text-sm mb-3">Before proceeding, please understand what will be deleted:</p>
                </AlertDescription>
              </Alert>

              {/* What will be deleted */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <Database className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Personal Data</p>
                    <p className="text-sm text-gray-600">All your profile information, preferences, and settings</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <Shield className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Ride History</p>
                    <p className="text-sm text-gray-600">All past rides, bookings, and travel records</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <CreditCard className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Payment Information</p>
                    <p className="text-sm text-gray-600">Saved payment methods and transaction history</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <XCircle className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Account Access</p>
                    <p className="text-sm text-gray-600">You will not be able to recover your account</p>
                  </div>
                </div>
              </div>
              
              {/* Confirmation Checkbox */}
              <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <input 
                    type="checkbox" 
                    id="confirmDelete" 
                    checked={isConfirmed}
                    onChange={(e) => {
                      setIsConfirmed(e.target.checked);
                      setDeleteError(''); // Clear error when checkbox state changes
                    }}
                    className="mt-1 h-5 w-5 text-red-600 focus:ring-red-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="confirmDelete" className="text-gray-800 font-medium cursor-pointer flex-1">
                    I understand that deleting my account is permanent and cannot be undone. I confirm that I want to proceed with the account deletion.
                  </label>
                </div>
              </div>
              
              {/* Error Message */}
              {deleteError && (
                <Alert className="bg-red-50 border-2 border-red-200 rounded-xl">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <AlertDescription className="text-red-800">
                    {deleteError}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
            
            <CardFooter className="flex flex-col sm:flex-row gap-3 bg-gray-50 px-6 py-5 border-t border-gray-200">
              <Button
                variant="destructive"
                className="w-full sm:w-auto sm:flex-1 h-12 text-base font-semibold bg-red-600 hover:bg-red-700 transition-all shadow-lg hover:shadow-xl"
                onClick={handleDeleteAccount}
                disabled={isDeleting || !isConfirmed}
              >
                {isDeleting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting Account...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Trash2 className="w-5 h-5" />
                    Delete My Account Permanently
                  </span>
                )}
              </Button>
              
            </CardFooter>
          </Card>

          
        </div>
      </div>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-green-100 rounded-full w-16 h-16 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Account Deleted Successfully
            </DialogTitle>
            <DialogDescription className="text-base text-gray-600 mt-2">
              Your account and all associated data have been permanently removed from our systems. You will be redirected to the home page.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center mt-4">
            <Button
              onClick={handleSuccessModalClose}
              className="w-full sm:w-auto px-8 bg-blue-600 hover:bg-blue-700 text-white"
            >
              Go to Home
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeleteAccount;
